// What MARK can put in a chat answer besides text (see "How to format" in mark.js): comparison
// cards, charts and follow-up buttons, written as fenced blocks. Charts never carry MARK's own
// numbers: he names what to chart and the site draws it from the trader's trades, so a chart
// can't show anything the journal doesn't.
import { choicesFor } from './compareSlices';
import { netPnl } from './pnl';
import { currentLocale } from './langState';

// Answers stream in; a block that hasn't closed yet shows as "pending" until it does.
export function parseReply(text) {
  const parts = [];
  const re = /```(cards|chart|next|steps|backup|rule)[^\n]*\n([\s\S]*?)(```|$)/g;
  let last = 0;
  let m;
  while ((m = re.exec(text))) {
    if (m.index > last) parts.push({ type: 'text', text: text.slice(last, m.index) });
    last = re.lastIndex;
    if (m[3] !== '```') {
      parts.push({ type: 'pending' });
      break;
    }
    let data = null;
    try {
      data = JSON.parse(m[2]);
    } catch {
      // A block MARK wrote badly is left out rather than shown as code.
    }
    if (m[1] === 'cards' && Array.isArray(data)) parts.push({ type: 'cards', cards: data.filter((c) => c && Array.isArray(c.rows)).slice(0, 6) });
    if (m[1] === 'chart' && data && typeof data === 'object') parts.push({ type: 'chart', spec: data });
    if (m[1] === 'next' && Array.isArray(data)) parts.push({ type: 'next', items: data.slice(0, 4) });
    // The plan: the #1 job and its steps, a backup for later, and a rule to add (mark.js SYSTEM).
    if (m[1] === 'steps' && data && Array.isArray(data.steps)) parts.push({ type: 'steps', job: String(data.job || ''), steps: data.steps.map(String).slice(0, 6) });
    if (m[1] === 'backup' && data && data.text) parts.push({ type: 'backup', title: String(data.title || ''), text: String(data.text) });
    if (m[1] === 'rule' && data && data.text) parts.push({ type: 'rule', text: String(data.text).slice(0, 200) });
  }
  if (last < text.length) parts.push({ type: 'text', text: text.slice(last) });
  return parts;
}

// Money MARK writes as +$1,234.56 or -$216 is shown green or red.
export const MONEY = /([+\-−]\$\d(?:[\d,]*\d)?(?:\.\d+)?)/;
export const moneyTone = (s) => (/^[-−]/.test(s) ? 'loss' : 'gain');

// Hours of the day in New York time, the way the rest of the site talks about sessions.
const nyHour = (t) => Number(new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', hour12: false, hour: '2-digit' }).format(new Date(t.date))) % 24;
const hourLabel = (h) => new Date(2026, 0, 1, h).toLocaleTimeString(currentLocale(), { hour: 'numeric' });

export const CHART_BY = ['month', 'week', 'day', 'weekday', 'session', 'hour', 'market', 'instrument', 'side', 'setup', 'account'];
export const CHART_MEASURES = ['net', 'winRate', 'expectancy', 'count'];

function groupsFor(by, trades, ctx) {
  const pick = (type, opts = {}) => choicesFor(type, trades, { ...ctx, ...opts }).map((c) => ({ label: c.label, list: trades.filter(c.test) }));
  switch (by) {
    case 'month': return pick('periods', { unit: 'month' }).reverse().slice(-12);
    case 'week': return pick('periods', { unit: 'week' }).reverse().slice(-12);
    case 'weekday': return pick('weekdays');
    case 'session': return pick('sessions');
    case 'market': return pick('markets');
    case 'instrument': return pick('instruments').slice(0, 8);
    case 'side': return pick('sides');
    case 'account': return pick('accounts');
    case 'setup': {
      const plays = pick('plays');
      if (plays.length) return plays;
      const setups = new Set((ctx.library || []).filter((x) => x.kind === 'setup').map((x) => x.name));
      return pick('tags').filter((g) => setups.has(g.label)).slice(0, 8);
    }
    case 'hour': {
      const timed = trades.filter((t) => !t.timeUnknown);
      const hours = [...new Set(timed.map(nyHour))].sort((a, b) => a - b);
      return hours.map((h) => ({ label: hourLabel(h), list: timed.filter((t) => nyHour(t) === h) }));
    }
    default: return [];
  }
}

function measure(list, kind) {
  const net = list.reduce((s, t) => s + netPnl(t), 0);
  if (kind === 'count') return list.length;
  if (kind === 'winRate') return (list.filter((t) => netPnl(t) > 0).length / list.length) * 100;
  if (kind === 'expectancy') return net / list.length;
  return net;
}

// The bars for one chart MARK asked for, or null if it doesn't fit the data.
export function chartRows(trades, spec, ctx = {}) {
  const by = CHART_BY.includes(spec && spec.by) ? spec.by : null;
  if (!by) return null;
  const kind = CHART_MEASURES.includes(spec.measure) ? spec.measure : 'net';
  const rows = groupsFor(by, trades.filter((t) => t.date), ctx)
    .filter((g) => g.list.length)
    .map((g) => ({ label: g.label, value: measure(g.list, kind), count: g.list.length }));
  return rows.length >= 2 ? { by, measure: kind, rows } : null;
}
